/**
 * Active Timers Database Helpers
 *
 * Firestore operations for active timer state.
 * Each mechanic can have at most ONE active timer (doc id = mechanicId).
 *
 * Collection path: teams/{teamId}/activeTimers/{mechanicId}
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  type CollectionReference,
  type DocumentReference,
  type Unsubscribe,
} from 'firebase/firestore';
import { setDoc } from '@/lib/firestore/writes';
import { db } from '@/lib/firebase';
import type { ActiveTimer, ActiveTimerDocument, StartTimerInput } from '../types/timeEntry';
import { timestampToIso } from '../types/timeEntry';

/**
 * Get collection reference for active timers within a team
 */
export function activeTimersCol(teamId: string): CollectionReference<ActiveTimerDocument> {
  return collection(
    db,
    'teams',
    teamId,
    'activeTimers'
  ) as CollectionReference<ActiveTimerDocument>;
}

/**
 * Get document reference for a mechanic's active timer
 * Doc ID = mechanicId (auth uid)
 */
export function activeTimerDoc(
  teamId: string,
  mechanicId: string
): DocumentReference<ActiveTimerDocument> {
  return doc(
    db,
    'teams',
    teamId,
    'activeTimers',
    mechanicId
  ) as DocumentReference<ActiveTimerDocument>;
}

/**
 * Convert Firestore document to ActiveTimer
 */
function convertActiveTimerDoc(data: ActiveTimerDocument): ActiveTimer {
  return {
    ...data,
    startedAt: timestampToIso(data.startedAt),
    updatedAt: timestampToIso(data.updatedAt),
  };
}

/**
 * Get a mechanic's active timer
 * Returns null if no timer is running
 */
export async function getActiveTimer(
  teamId: string,
  mechanicId: string
): Promise<ActiveTimer | null> {
  const docRef = activeTimerDoc(teamId, mechanicId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  return convertActiveTimerDoc(docSnap.data());
}

/**
 * Listen to a mechanic's active timer (realtime updates)
 * Returns an unsubscribe function
 */
export function listenActiveTimer(
  teamId: string,
  mechanicId: string,
  callback: (timer: ActiveTimer | null) => void
): Unsubscribe {
  const docRef = activeTimerDoc(teamId, mechanicId);
  return onSnapshot(docRef, (docSnap) => {
    if (!docSnap.exists()) {
      callback(null);
    } else {
      callback(convertActiveTimerDoc(docSnap.data()));
    }
  });
}

/**
 * List all active timers for a team
 * Useful for "who is working" dashboard view
 */
export async function listActiveTimers(teamId: string): Promise<ActiveTimer[]> {
  const col = activeTimersCol(teamId);
  const snapshot = await getDocs(col);
  return snapshot.docs.map((doc) => convertActiveTimerDoc(doc.data()));
}

/**
 * Create or overwrite an active timer for a mechanic
 * Used internally by startWorkOrderTimer
 */
export async function setActiveTimer(
  teamId: string,
  mechanicId: string,
  mechanicName: string | undefined,
  input: StartTimerInput
): Promise<ActiveTimer> {
  const docRef = activeTimerDoc(teamId, mechanicId);

  const data: ActiveTimerDocument = {
    mechanicId,
    mechanicName,
    workOrderId: input.workOrderId,
    workOrderTitle: input.workOrderTitle,
    vehicleId: input.vehicleId,
    vehiclePlate: input.vehiclePlate,
    startedAt: serverTimestamp() as unknown as Timestamp,
    hourlyCostSnapshot: input.hourlyCostSnapshot,
    hourlySellSnapshot: input.hourlySellSnapshot,
    updatedAt: serverTimestamp() as unknown as Timestamp,
  };

  await setDoc(docRef, data as any);

  // Return the timer with current timestamp (approximate)
  return {
    ...data,
    startedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Delete a mechanic's active timer
 * Used internally by stopWorkOrderTimer
 */
export async function deleteActiveTimer(teamId: string, mechanicId: string): Promise<void> {
  const docRef = activeTimerDoc(teamId, mechanicId);
  await deleteDoc(docRef);
}

/**
 * Check if a mechanic has an active timer
 */
export async function hasActiveTimer(teamId: string, mechanicId: string): Promise<boolean> {
  const timer = await getActiveTimer(teamId, mechanicId);
  return timer !== null;
}
